import React, { useState, useRef, useEffect } from "react";
import { FaTasks, FaBookOpen, FaTimesCircle } from "react-icons/fa";

const HomeworkInput = ({
  studentid,
  formData,
  handleInputChange,
  inputRefs,
  showSuggestions,
  setShowSuggestions,
  handleFocus,
  handleBlur,
  getUniqueSuggestions
}) => {

  const [isPageMode, setIsPageMode] = useState(false); // 📖 ページ入力モード
  const [pageInput, setPageInput] = useState(""); // ページ入力用
  const [homeworkText, setHomeworkText] = useState(formData[studentid]?.homework || "");
  const inputRef = useRef(null); // 🔹 フォーカス維持用

  // 🔹 生徒が切り替わるたびに `homeworkText` を最新の `formData` に同期
  useEffect(() => {
    setHomeworkText(formData[studentid]?.homework || "");
  }, [studentid, formData]);

  // 📖 ボタンでページ入力モードに切り替え
  const togglePageMode = () => {
    setIsPageMode(true);
    setPageInput(""); // 初期化
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
      }
    }, 0); // 🔹 フォーカス維持 & カーソルを末尾に
  };

  // ページ入力時の処理
  const handlePageInputChange = (e) => {
    let value = e.target.value.replace(/[^0-9,〜P]/g, ""); // 数字,カンマ,〜,P のみ許可
    if (value && !value.startsWith("P")) value = "P" + value; // P を自動補完
    setPageInput(value);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
      }
    }, 0); // 🔹 カーソルを末尾に
  };

  // ➡ ボタンで範囲指定「〜」を追加
  const addRange = () => {
    if (pageInput.match(/P\d+$/)) {
      setPageInput((prev) => prev + "〜");
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
        }
      }, 0); // 🔹 フォーカス維持 & カーソル末尾
    }
  };

  // ⏎ ボタン（Enterキー）で次のページ候補を追加
  const addNextPage = () => {
    const lastNum = parseInt(pageInput.match(/\d+$/)?.[0]);
    if (lastNum) {
      setPageInput((prev) => prev + `, P${lastNum + 1}`);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
        }
      }, 0); // 🔹 フォーカス維持 & カーソル末尾
    }
  };

  // ✅ 確定ボタンで自由入力に戻る
  const confirmPages = () => {
    const newHomeworkText = homeworkText ? `${homeworkText} ${pageInput}` : pageInput;
    setHomeworkText(newHomeworkText);
    handleInputChange("homework", newHomeworkText);
    setIsPageMode(false);
  };

  return (
    <div className="mb-4 border-b pb-3">
      {/* 🔹 ラベル部分 */}
      <label className="block text-lg font-bold flex items-center">
        <FaTasks className="mr-2 text-green-500" /> 宿題
      </label>

      <div className="relative flex items-center">
        {isPageMode ? (
          <div className="flex items-center border rounded-lg p-2 w-full gap-2 shadow-sm">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pageInput}
              onChange={handlePageInputChange}
              placeholder="P25"
              className="w-full border-none focus:ring-0"
            />
            <button className="px-2 text-blue-500 hover:text-blue-700" onClick={addRange}>➡</button>
            <button className="px-2 text-green-500 hover:text-green-700" onClick={addNextPage}>⏎</button>
            <button className="px-2 text-red-500 hover:text-red-700" onClick={confirmPages}>✅</button>
          </div>
        ) : (
          <div className="relative w-full">
            {/* ✅ `textarea` に変更して `rows={2}` で2行固定 */}
            <textarea
              ref={inputRef}
              className="w-full p-2 border rounded-lg shadow-sm resize-none"
              rows={2} // 🔹 2行固定
              value={homeworkText}
              onChange={(e) => {
                setHomeworkText(e.target.value);
                handleInputChange("homework", e.target.value);
                if (e.target.value !== "") {
                  setShowSuggestions("homework", false); // 入力が始まったらオートコンプリートを非表示
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && inputRefs?.current["homework"]?.focus()} // 🔹 Enter で次の入力欄へ
              onFocus={e => handleFocus("homework", e.target.value)}
              onBlur={() => handleBlur("homework")}
            />

            {/* 🔹 オートコンプリートリスト */}
            {showSuggestions.homework && getUniqueSuggestions("homework").length > 0 && (
              <ul className="absolute top-full left-0 w-full bg-white border rounded-lg shadow-md z-10 max-h-40 overflow-y-auto">
                {getUniqueSuggestions("homework").map((suggestion, index) => (
                  <li
                    key={index}
                    className="p-2 hover:bg-gray-200 cursor-pointer"
                    onMouseDown={() => handleInputChange("homework", suggestion)}
                  >
                    {suggestion.length > 17 ? `${suggestion.substring(0, 17)}...` : suggestion}
                  </li>
                ))}
              </ul>
            )}

            <button
              className="absolute right-10 top-3 transform text-gray-400 hover:text-gray-600"
              onClick={togglePageMode}
            >
              <FaBookOpen />
            </button>
            {homeworkText && (
              <button
                className="absolute right-3 top-3 text-gray-400 hover:text-red-600"
                onClick={() => {
                  setHomeworkText("");
                  handleInputChange("homework", "");
                }}
              >
                <FaTimesCircle />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeworkInput;
